/**
 * Authentication Service
 * Handles user authentication and token management
 */

import { CONFIG } from '../config/constants.js';

class AuthService {
  constructor() {
    this.accessToken = null;
  }

  /**
   * Check current authentication state using existing Excel session
   */
  async checkAuthenticationState() {
    try {
      // Check if IdentityAPI is supported
      if (!Office.context.requirements.isSetSupported("IdentityAPI", CONFIG.OFFICE.IDENTITY_API_VERSION)) {
        console.log('IdentityAPI not supported. Possible causes: Office version too old, not running in Excel, or missing permissions.');
        return {
          isAuthenticated: false,
          message: 'Please login with your Microsoft account in Excel to use this add-in.'
        };
      }

      // Try to get access token from existing Excel session
      const result = await Office.auth.getAccessToken({ 
        allowSignInPrompt: true,   // Allow sign-in prompt if user not signed in
        allowConsentPrompt: true   // Allow consent prompt if consent not granted
      });

      if (result) {
        console.log('User is already logged in');
        this.accessToken = result;
        return {
          isAuthenticated: true,
          token: result,
          userInfo: this.parseJwtToken(result)
        };
      } else {
        console.log('No existing session found.');
        return {
          isAuthenticated: false,
          message: 'Please login with your Microsoft account in Excel to use this add-in.'
        };
      }
    } catch (error) {
      console.log('Authentication check failed:', error.message, 'Code:', error.code);
      this.logAuthError(error.code);
      return {
        isAuthenticated: false,
        message: 'Please login with your Microsoft account in Excel to use this add-in.',
        error: error
      };
    }
  }

  /**
   * Log authentication error details
   */
  logAuthError(errorCode) {
    const errorMessages = {
      [CONFIG.ERROR_CODES.USER_NOT_SIGNED_IN]: 'User is not signed in to Office.',
      [CONFIG.ERROR_CODES.CONSENT_REQUIRED]: 'Consent required for additional scopes.',
      [CONFIG.ERROR_CODES.RUNTIME_ERROR]: 'Office.js runtime error or unsupported environment.'
    };

    const message = errorMessages[errorCode] || 'Unknown authentication error.';
    console.log('Cause:', message);
  }

  /**
   * Parse JWT token to extract user information
   */
  parseJwtToken(token) {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) {
        throw new Error('Invalid token format');
      }
      
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      throw error;
    }
  }

  /**
   * Get username from token
   */
  getUsername() {
    if (!this.accessToken) {
      return 'User';
    }

    try {
      const payload = this.parseJwtToken(this.accessToken);
      return payload.email || 
             payload.name || 
             payload.given_name || 
             payload.preferred_username || 
             payload.upn || 
             'User';
    } catch (error) {
      console.log("Could not parse username from token:", error);
      return 'User';
    }
  }

  /**
   * Get current access token
   */
  getAccessToken() {
    return this.accessToken;
  }

  /**
   * Set access token
   */
  setAccessToken(token) {
    this.accessToken = token;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return this.accessToken !== null;
  }

  /**
   * Clear authentication
   */
  clearAuth() {
    this.accessToken = null;
  }
}

// Export singleton instance
export const authService = new AuthService();
